// Every order-paperwork endpoint, in one place, so auth and the base-URL rule
// live once instead of in each component.
//
// The backend is the only thing that writes a signed document: `scanOrderDoc`
// proposes field boxes and values, the clinician confirms them on screen, and
// `applyOrderDoc` stamps what they confirmed onto the ORIGINAL file and saves a
// sibling `<stem>__signed.pdf` (`<stem>__filled.pdf` when saved unsigned).
// Nothing here produces a PDF.
import { BASE_URLS, bucketName_files } from "../components/env";
import { getSupabase } from "../components/supabaseClient";
import { isImpersonating } from "../utils/impersonation";

// Shown instead of the server's word, "unauthenticated", which describes the
// server's view rather than anything the reader can do about it.
export const SESSION_EXPIRED =
  "Your session has expired. Reload the page, and sign in again if that does not fix it.";

export async function getAccessTokenSafe() {
  try {
    const supabase = await getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data && data.session ? data.session.access_token : null;
  } catch (err) {
    console.warn("orderPaperwork: getAccessToken failed", err);
    return null;
  }
}

// Ask Supabase to mint a new access token from the refresh token it still
// holds. The stored access token is short-lived and rotates out from under a
// long-open tab; the refresh token usually outlives it. Never in a "view as"
// tab: its session holds no real refresh token, so a refresh always fails, and
// auth-js then drops the session, which would end the view-as.
async function refreshAccessToken() {
  if (isImpersonating()) return null;
  try {
    const supabase = await getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.refreshSession();
    return data && data.session ? data.session.access_token : null;
  } catch (err) {
    console.warn("orderPaperwork: refreshSession failed", err);
    return null;
  }
}

/**
 * A backend call that carries the caller's identity.
 *
 * Never sends the request without a token: an anonymous call to a JWT-gated
 * endpoint is a guaranteed 401, and it surfaced to clinicians as the bare word
 * "unauthenticated". One refresh is attempted before giving up, and one retry
 * after a 401, because an expired-but-refreshable token is the ordinary case.
 */
export async function authedFetch(path, { method = "POST", body } = {}) {
  let token = await getAccessTokenSafe();
  if (!token) token = await refreshAccessToken();
  if (!token) throw new Error(SESSION_EXPIRED);

  const send = (bearer) =>
    fetch(`${backendBase()}/api/${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer}`,
      },
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });

  let res = await send(token);
  if (res.status === 401) {
    // One retry only. A second 401 means the refresh token is gone too, and
    // retrying again would just spin.
    const fresh = await refreshAccessToken();
    if (!fresh) throw new Error(SESSION_EXPIRED);
    res = await send(fresh);
    if (res.status === 401) throw new Error(SESSION_EXPIRED);
  }

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(payload?.error || `Request failed (${res.status}).`);
    err.payload = payload;
    err.status = res.status;
    throw err;
  }
  return payload;
}

export function backendBase() {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return BASE_URLS.local.backend;
  return (
    (process.env.REACT_APP_BACKEND_URL &&
      process.env.REACT_APP_BACKEND_URL.trim() !== "" &&
      process.env.REACT_APP_BACKEND_URL) ||
    BASE_URLS.render.backend
  );
}

const call = authedFetch;

// Two Claude vision calls per page, so this is slow — count on tens of seconds
// for a multi-page fax. `force` re-runs detection instead of returning the
// stored field map.
// `orderIds` narrows the fill to the items the doctor ticked. Omitted, the
// form is filled from everything on the patient's record.
// `paperworkId`: the Paperwork-tab form being read — what lets someone the
// form was handed to (without the Orders role) read it.
export const scanOrderDoc = ({ patientId, path, force = false, orderIds = null, paperworkId }) =>
  call("clinic/order-docs/scan/", {
    body: {
      patient_id: patientId, path, force, order_ids: orderIds || [],
      ...(paperworkId ? { paperwork_id: paperworkId } : {}),
    },
  });

// `paperworkId`: a Paperwork-tab form being saved — the save updates that form.
export const applyOrderDoc = ({ patientId, path, fields, signature, paperworkId }) =>
  call("clinic/order-docs/apply/", {
    body: {
      patient_id: patientId, path, fields, signature,
      ...(paperworkId ? { paperwork_id: paperworkId } : {}),
    },
  });

export const getMySignature = () => call("clinic/signature/", { method: "GET" });

export const saveMySignature = ({ png, signerName }) =>
  call("clinic/signature/save/", {
    body: { signature_png: png, signer_name: signerName },
  });

// ─── order form templates ────────────────────────────────────────────────────
// Blank forms live in the files bucket, shared per schema group:
//   New/templates/orders_templates/{group}/{name}.pdf
// A clinic with no group set reads a folder named after its own schema.

// `orderIds` is optional; supplying it also returns `suggested`, the template
// the AI thinks fits those orders. The doctor still chooses.
export const listOrderTemplates = ({ patientId, orderIds }) =>
  call("clinic/order-templates/list/", {
    body: { patient_id: patientId, order_ids: orderIds || [] },
  });

// Super Admin only — the library across every group.
export const listAllOrderTemplates = () =>
  call("clinic/order-templates/admin/", { method: "GET" });

export const uploadOrderTemplate = ({ group, filename, fileBase64 }) =>
  call("clinic/order-templates/upload/", {
    body: { group, filename, file_base64: fileBase64 },
  });

export const deleteOrderTemplate = ({ group, name }) =>
  call("clinic/order-templates/delete/", { body: { group, name } });

// ─── sending a finished order out of the clinic ──────────────────────────────
// Two destinations, two very different transports:
//   fax   — SRFax, via the SAME `/api/fax/send/` the Admin page's Send tab
//           uses. It takes a storage path, so a signed order needs no special
//           handling; the result lands in Admin ▸ Fax Inbox ▸ Sent.
//   email — Zoho SMTP, with the PDF attached. New endpoint: nothing else here
//           could put a stored file in an email.

export const listSignedOrders = ({ patientId }) =>
  call("clinic/order-docs/signed/", { body: { patient_id: patientId } });

// The file, its order_paperwork row and its Reports cache row all move
// together — the backend does all three.
export const renameOrderDoc = ({ patientId, path, filename }) =>
  call("clinic/order-docs/rename/", {
    body: { patient_id: patientId, path, filename },
  });

export const emailOrderDoc = ({ patientId, path, to, subject, message }) =>
  call("clinic/order-docs/email/", {
    body: { patient_id: patientId, path, to, subject, message },
  });

// The fax endpoints live outside this module's `call` helper: they are the
// Admin page's, they are not JWT-gated, and `/api/config/` is plain GET.
async function faxCall(path, { method = "GET", body } = {}) {
  const res = await fetch(`${backendBase()}/api/${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload?.success === false) {
    const err = new Error(payload?.error || `Request failed (${res.status}).`);
    err.payload = payload;
    err.status = res.status;
    throw err;
  }
  return payload;
}

// Who is sending. The fax contacts list is per-user, and SRFax records a
// "sent by" against the queue entry, so both need the signed-in account.
export async function getCurrentUser() {
  try {
    const supabase = await getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    const user = data && data.session ? data.session.user : null;
    if (!user) return null;
    return { id: user.id, email: user.email || "" };
  } catch (err) {
    console.warn("orderPaperwork: getCurrentUser failed", err);
    return null;
  }
}

// The saved recipients from Admin ▸ Fax Inbox ▸ Contacts, scoped to the clinic.
export const listFaxContacts = ({ userId, schema }) =>
  faxCall(
    `fax/contacts/?user_id=${encodeURIComponent(userId || "")}` +
      `&schema=${encodeURIComponent(schema || "public")}`
  );

// SRFax will only accept a send whose sender address is one it has on file.
export const listFaxSenderEmails = async () => {
  const cfg = await faxCall("config/");
  return Array.isArray(cfg?.srfaxAuthEmails) ? cfg.srfaxAuthEmails : [];
};

export const sendOrderFax = ({
  path, faxNumber, recipientName, senderName, senderFax, senderEmail,
  subject, coverPage, coverPageText, sentBy, sentByUserId, schema,
}) =>
  faxCall("fax/send/", {
    method: "POST",
    body: {
      file_path: path,
      fax_number: faxNumber,
      recipient_name: recipientName || "",
      sender_name: senderName || "",
      sender_fax: senderFax || "",
      ...(senderEmail ? { sender_email: senderEmail } : {}),
      subject: subject || "",
      cover_page: !!coverPage,
      cover_page_text: coverPageText || "",
      sent_by: sentBy || null,
      sent_by_user_id: sentByUserId || null,
      schema: schema || "public",
    },
  });

// ── Forms on a visit (Paperwork tab review workflow) ───────────────────────
// One form = one paperwork record; its current file changes as it is filled,
// uploaded and signed. `patientId` is the visit's chart id.
export const listPaperwork = ({ patientId }) =>
  call("clinic/paperwork/list/", { body: { patient_id: patientId } });

export const getPaperworkItem = ({ patientId, paperworkId }) =>
  call("clinic/paperwork/item/", { body: { patient_id: patientId, paperwork_id: paperworkId } });

export const startFromTemplate = ({ patientId, templatePath }) =>
  call("clinic/paperwork/from-template/", { body: { patient_id: patientId, template_path: templatePath } });

// Stamps the caller's saved signature on the form's current file, or
// `signature` ({ png, signer_name }) drawn just now. With neither, it fails
// with err.payload.needs_signature; a form already signed fails with
// err.payload.already_signed.
export const signPaperwork = ({ patientId, paperworkId, signature }) =>
  call("clinic/paperwork/sign/", {
    body: { patient_id: patientId, paperwork_id: paperworkId, ...(signature ? { signature } : {}) },
  });

// A hand-filled PDF or photo: straight from the browser to storage through a
// signed URL (it never passes through the server), then made the form's
// current file.
export async function uploadPaperworkVersion({ patientId, paperworkId, file }) {
  const ticket = await call("clinic/paperwork/upload-url/", {
    body: { patient_id: patientId, paperwork_id: paperworkId, filename: file.name, size: file.size },
  });
  const supabase = await getSupabase();
  const { error } = await supabase.storage.from(bucketName_files || "files")
    .uploadToSignedUrl(ticket.path, ticket.token, file, { contentType: ticket.content_type, upsert: false });
  if (error) throw new Error(error.message || "Upload failed");
  return call("clinic/paperwork/upload-done/", {
    body: { patient_id: patientId, paperwork_id: paperworkId, path: ticket.path },
  });
}
